import { useMemo, useState } from "react";
import { ChevronsDownUp, ChevronsUpDown, Copy } from "lucide-react";
import { JsonView } from "react-json-view-lite";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { formatJson, toJsonData } from "@/lib/logFormat";
import { cn } from "@/lib/utils";

type JsonViewStyles = Required<NonNullable<React.ComponentProps<typeof JsonView>["style"]>>;

const JSON_TREE_STYLES: Omit<JsonViewStyles, "ariaLables"> = {
  container: "font-mono text-sm leading-relaxed break-words whitespace-pre-wrap text-gray-700",
  basicChildStyle: "m-0 p-0",
  childFieldsContainer: "m-0 list-none border-l border-gray-200 pl-3",
  label: "mr-1 font-semibold text-gray-500",
  clickableLabel: "mr-1 cursor-pointer font-semibold text-gray-500 hover:text-gray-700",
  nullValue: "text-gray-400 italic",
  undefinedValue: "text-gray-400 italic",
  stringValue: "text-emerald-700",
  numberValue: "text-blue-600",
  booleanValue: "text-purple-600",
  otherValue: "text-gray-700",
  punctuation: "text-gray-400",
  expandIcon:
    "mr-1 cursor-pointer text-gray-400 select-none after:content-['▸'] hover:text-gray-600",
  collapseIcon:
    "mr-1 cursor-pointer text-gray-400 select-none after:content-['▾'] hover:text-gray-600",
  collapsedContent: "mr-1 cursor-pointer text-gray-400 after:text-[0.8em] after:content-['…']",
  noQuotesForStringValues: false,
  quotesForFieldNames: false,
  stringifyStringValues: false,
};

const AUTO_EXPAND_MAX_CHARS = 200;

const AUTO_EXPAND_MIN_LEVELS = 2;

type ExpandMode = "auto" | "all" | "none";

const EXPANDERS: Record<ExpandMode, (level: number, value: unknown) => boolean> = {
  auto: (level, value) => {
    if (level < AUTO_EXPAND_MIN_LEVELS) return true;
    try {
      return JSON.stringify(value).length <= AUTO_EXPAND_MAX_CHARS;
    } catch {
      return false;
    }
  },
  all: () => true,
  none: (level) => level < 1,
};

export const JsonTree = ({ value, className }: { value: unknown; className?: string }) => {
  const { t } = useTranslation("shared");
  const data = useMemo(() => toJsonData(value), [value]);
  const styles = useMemo<JsonViewStyles>(
    () => ({
      ...JSON_TREE_STYLES,
      ariaLables: { collapseJson: t("jsonTree.collapse"), expandJson: t("jsonTree.expand") },
    }),
    [t],
  );
  const [mode, setMode] = useState<ExpandMode>("auto");
  const shouldExpandNode = useMemo(() => EXPANDERS[mode], [mode]);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(formatJson(value));
    toast.success(t("jsonTree.copySuccess"));
  };

  const allExpanded = mode === "all";

  return (
    <div
      className={cn(
        "relative overflow-auto rounded-md bg-gray-50 px-3 py-2 ring-1 ring-gray-200",
        className,
      )}
    >
      <div className="sticky top-0 right-0 z-10 flex justify-end gap-1">
        {data != null && (
          <JsonTreeAction
            label={allExpanded ? t("jsonTree.collapseAll") : t("jsonTree.expandAll")}
            onClick={() => setMode(allExpanded ? "none" : "all")}
          >
            {allExpanded ? (
              <ChevronsDownUp className="size-3.5" />
            ) : (
              <ChevronsUpDown className="size-3.5" />
            )}
          </JsonTreeAction>
        )}
        <JsonTreeAction label={t("jsonTree.copy")} onClick={copyToClipboard}>
          <Copy className="size-3.5" />
        </JsonTreeAction>
      </div>

      {data != null ? (
        <JsonView data={data} style={styles} shouldExpandNode={shouldExpandNode} />
      ) : (
        <pre className="font-mono text-sm leading-relaxed break-words whitespace-pre-wrap text-gray-700">
          {formatJson(value)}
        </pre>
      )}
    </div>
  );
};

const JsonTreeAction = ({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    onClick={onClick}
    className="rounded-md bg-gray-50 p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-600 focus:outline-hidden"
  >
    {children}
  </button>
);
